import { FC, useState } from "react";
import {
  useAlertStore,
  useGeneralStore,
  usePopupStore,
  useThemeStore,
} from "../../../stores";
import { CashDetails, CashQueryForm } from "../../../components/cash";
import { CashQuery, CashType, Customer, Timestamp } from "../../../types";
import { CashQueryFormPropsSetters } from "../../../components/cash/cash-query-form/types";
import { addComma, cashTypeToString, getRangeLastMonth } from "../../../utils";
import { Column, Row } from "../../../components/containers";
import {
  AlignItems,
  JustifyContent,
} from "../../../components/containers/types";
import { Button } from "../../../components/buttons";
import { Heading, Text } from "../../../components/typography";
import { Cash, CashTable } from "../../../types";
import { CashApi } from "../../../api";
import Table from "../../../components/table";
import { TableRendererProps } from "../../../components/table/types";

const INIT_CASH_QUERY_STATE: CashQuery = {
  customer: null,
  type: null,
  description: "",
  total_price: 0,
  fromDate: getRangeLastMonth().from,
  toDate: getRangeLastMonth().to,
};

const SearchCash: FC = () => {
  const theme = useThemeStore((s) => s.theme);
  const getCustomerById = useGeneralStore((s) => s.getCustomerById);
  const setPopupBody = usePopupStore((s) => s.setPopupBody);

  const [cashQuery, setCashQuery] = useState<CashQuery>({
    ...INIT_CASH_QUERY_STATE,
  });

  const [loading, setLoading] = useState<boolean>(false);

  const getSetters = (): CashQueryFormPropsSetters => ({
    setCustomer: (v) =>
      setCashQuery((prev) => ({ ...prev, customer: v as Customer | null })),
    setType: (v) =>
      setCashQuery((prev) => ({ ...prev, type: v as CashType | null })),
    setDescription: (v) =>
      setCashQuery((prev) => ({ ...prev, description: v as string })),
    setTotalPrice: (v) =>
      setCashQuery((prev) => ({ ...prev, total_price: v as number })),
    setFromDate: (v) =>
      setCashQuery((prev) => ({ ...prev, fromDate: v as Timestamp })),
    setToDate: (v) =>
      setCashQuery((prev) => ({ ...prev, toDate: v as Timestamp })),
  });

  const [cashRes, setCashRes] = useState<Cash[]>([]);
  const pushAlert = useAlertStore((s) => s.pushAlert);

  const onSearch = (): void => {
    setLoading(true);
    setCashRes([]);

    CashApi.query(cashQuery)
      .then((res) => {
        setCashRes(res);
      })
      .catch((err) => {
        pushAlert(err.msg || "unknown error", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <div
        style={{
          background: theme.overlay,
          borderRadius: 15,
          minHeight: 200,
          marginTop: "1rem",
          padding: "1rem",
        }}
      >
        <CashQueryForm cashQuery={cashQuery} {...getSetters()} />

        <Row style={{ width: "100%" }} justifyContent={JustifyContent.End}>
          <Button
            loading={loading}
            onClick={onSearch}
            background={theme.primary}
            color={theme.textOnprimary}
          >
            جستحو
          </Button>
        </Row>
      </div>

      <Column
        style={{ minHeight: 70, marginTop: "1rem" }}
        alignItems={AlignItems.Start}
        gap={10}
      >
        <Heading size={18} color={theme.textOnBg}>
          نتایج
        </Heading>

        <div
          style={{
            overflowX: "auto",
            width: "calc(100% - 20px)",
            background: theme.overlay,
            padding: "20px 10px",
            borderRadius: 15,
          }}
        >
          <Table<CashTable>
            data={cashRes}
            label={{
              customer: "مشتری",
              description: "بابت",
              total_price: "کل مبلغ",
              type: "نوع نقدی",
            }}
            renderer={{
              customer: ({ props }: TableRendererProps) => (
                <Text size={12} color={theme.textOnOverlay}>
                  {getCustomerById(props?.$oid || "")?.fullname || "---"}
                </Text>
              ),
              description: ({ props }: TableRendererProps) => (
                <Text size={12} color={theme.textOnOverlay}>
                  {props}
                </Text>
              ),
              total_price: ({ props }: TableRendererProps) => (
                <Text size={12} color={theme.textOnOverlay}>
                  {addComma(props)}
                </Text>
              ),
              type: ({ props }: TableRendererProps) => (
                <Text size={12} color={theme.textOnOverlay}>
                  {cashTypeToString(props)}
                </Text>
              ),
            }}
            layout={{
              customer: 200,
              description: 200,
              total_price: 150,
              type: 120,
            }}
            rowEvents={[
              {
                type: "click",
                handler: (e, data) => {
                  setPopupBody(
                    <CashDetails cash={data as Cash} updateCb={onSearch} />
                  );
                },
              },
            ]}
          />
        </div>
      </Column>
    </>
  );
};

export default SearchCash;
